import React from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PuffinLoader } from './ui/PuffinLoader';
import Navbar from './Navbar';
import DynamicSidebar from './DynamicSidebar';
import ScrollToTop from './ScrollToTop';
import EnhancedScrollToTop from './EnhancedScrollToTop';
import MessageNotificationBadge from './MessageNotificationBadge';
import EnableNotifications from './EnableNotifications';
import { useNavigate } from 'react-router-dom';

const Layout = ({ children }) => {
  const { user, isLoading, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(true);

  const handleLogout = async () => {
    localStorage.removeItem("token");
    navigate("/login");
    await logout();
  };

  // Auto-logout for GMD and System Admin after 5 minutes of inactivity
  React.useEffect(() => {
    if (!user) return;

    const userTypeName = user.UserType?.name?.toLowerCase();
    const isGMDOrAdmin = userTypeName === 'gmd' || userTypeName === 'system administrator';

    if (!isGMDOrAdmin) return;

    let timeoutId;
    const TIMEOUT_DURATION = 5 * 60 * 1000; // 5 minutes

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        handleLogout();
      }, TIMEOUT_DURATION);
    };

    // Events that indicate user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    // Start the timer
    resetTimer();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [user]);
  // console.log('🏠 Layout render - Auth state:', {
  //   isLoading,
  //   isAuthenticated,
  //   user: user ? {
  //     id: user.id,
  //     email: user.email,
  //     first_name: user.first_name,
  //     last_name: user.last_name,
  //     UserType: user.UserType ? {
  //       name: user.UserType.name,
  //       sidebar_modules: user.UserType.sidebar_modules ? 'Present' : 'Missing'
  //     } : 'Missing',
  //     Role: user.Role ? { name: user.Role.name } : 'Missing'
  //   } : 'Missing'
  // });

  if (isLoading) {
    // console.log('⏳ Layout - Still loading, showing loader');
    return (
      <div className="flex justify-center items-center h-screen">
        <PuffinLoader size="lg" message="Loading application..." />
      </div>
    );
  }
  
  if (!isAuthenticated || !user) {
    // console.log('❌ Layout - Not authenticated or no user, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Enhanced scroll to top on route change */}
      <EnhancedScrollToTop 
        behavior="smooth" 
        delay={100} 
        selector="main"
        showIndicator={true}
        indicatorThreshold={300}
      />
      
      {/* Dynamic Sidebar - shows modules based on user's user_type */}
      <DynamicSidebar user={user} isCollapsed={isSidebarCollapsed} setIsCollapsed={setIsSidebarCollapsed} />
      
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Navbar */}
        <Navbar user={user} onLogout={handleLogout} isSidebarCollapsed={isSidebarCollapsed} />

        {/* Opt-in native push notification banner (WhatsApp-style) */}
        <EnableNotifications user={user} />

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-6 pt-[88px]">
          <Outlet />
        </main>
      </div>

      {/* Floating Message Notification Button - Global */}
      <MessageNotificationBadge />
    </div>
  );
};

export default Layout;